import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { decodeEventText, decodeEventTextOptional } from "@shared/eventText";
import { AppShell } from "@/components/AppShell";
import { DotRail } from "@/components/DotRail";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { shareCalendarInvite } from "@/lib/calendarInvite";
import { AddToCalendarMenu } from "@/components/AddToCalendarMenu";
import { CenteredPillRow, type CenteredPillItem } from "@/components/dexVariantVisuals";
import { EVENTS, type EventSpawn, type GameEvent } from "@/lib/data";
import { EventDexGaps } from "@/components/EventDexGaps";
import { isMaxBattleEventCategory } from "@/lib/raidDexCrossRef";
import { ProLock } from "@/components/ProLock";
import { useSession, hasProAccess } from "@/components/SessionProvider";
import {
  EVENT_DEX_GAPS_EVENT,
  isEventDexGapsEnabled,
  setEventDexGapsEnabled,
} from "@/lib/eventDexGapsPref";
import {
  eventLocalDate,
  eventDomId,
  findActiveDayEvent,
  findExactDayEvent,
  dayCellAccessibleLabel, consumeCalendarEvent } from "@/lib/calendarDayScroll";
import { eventCountdownMs, eventCountdownDate } from "@/lib/calendarLinks";
import {
  Calendar as CalendarIcon, Check, Sparkles,
  ChevronRight, Bell, X, Gift, MapPin,
} from "lucide-react";
import { MenuHeading } from "@/components/MenuHeading";
import { EventTypePill, eventTypeMeta } from "@/components/EventTypePill";
import { EventSearchIcon } from "@/components/EventSearchIcon";
import { eventDexSearchHref } from "@/lib/eventSearchLink";
import { HeroCard, HeroCardMedia } from "@/components/ui/HeroCard";
import { ExternalLink } from "lucide-react";
import { EventRemindButton } from "@/components/EventRemindButton";
import { armSubscribedEvents } from "@/lib/liveEventArming";
import {
  CALENDAR_TODO_EVENT,
  listCalendarTodos,
  toggleCalendarTodo,
  removeCalendarTodo,
  type CalendarTodoItem,
} from "@/lib/calendarTodoStore";
import { STICKY_ROW_Z, STICKY_UNDER_HEADER } from "@/lib/stickyOffsets";
import { eventsLiveKey } from "@/lib/eventsLiveKey";
import { hydrateAttendingFromAccount, toggleAttending, useAttendingIds } from "@/lib/eventAttending";
import { pickTodayFocus } from "@/lib/todayFocus";
import { BonusBadges } from "@/components/BonusBadges";
import { activeDailyBonusEvents, mergedBonuses } from "@/lib/eventBonuses";

// b15b U6: subscribe to the persistent Calendar to-do store. Re-reads on the
// CALENDAR_TODO_EVENT broadcast so every Calendar surface (focus card, upcoming
// rows, the plan list) stays in sync when an item is ticked off.
function useCalendarTodos(): CalendarTodoItem[] {
  const [items, setItems] = useState<CalendarTodoItem[]>(() => listCalendarTodos());
  useEffect(() => {
    if (typeof window === "undefined") return;
    const refresh = () => setItems(listCalendarTodos());
    refresh();
    window.addEventListener(CALENDAR_TODO_EVENT, refresh);
    return () => window.removeEventListener(CALENDAR_TODO_EVENT, refresh);
  }, []);
  return items;
}

// b13g G1: resolve a GameEvent category to its `--event-color-*` token so the
// HeroCard glow border carries the same colour signal as the b13e type pill.
function eventGlowColor(category: GameEvent["category"]): string {
  return `var(${eventTypeMeta(category).token})`;
}

// b13g G1: countdown footer line ("14h 36m left" / "Starts in 2d"), mirroring
// the RightNowCard window logic so the hero footer matches the OLD card.
function eventCountdown(ev: GameEvent, now: Date = new Date()): string | null {
  const start = eventCountdownDate(ev.start);
  const end = ev.end ? eventCountdownDate(ev.end) : null;
  const t = now.getTime();
  if (Number.isNaN(start.getTime())) return null;
  if (end && !Number.isNaN(end.getTime()) && end.getTime() < t) return "Wrapped up";
  if (start.getTime() > t) {
    const mins = Math.round((start.getTime() - t) / 60000);
    if (mins < 60) return `Starts in ${Math.max(1, mins)}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Starts in ${hrs}h ${mins % 60}m`;
    return `Starts in ${Math.round(hrs / 24)}d`;
  }
  if (!end || Number.isNaN(end.getTime())) return "Active now";
  const remMs = end.getTime() - t;
  const hrs = Math.floor(remMs / 36e5);
  const mins = Math.floor((remMs % 36e5) / 60000);
  if (hrs < 1) return `${Math.max(1, mins)}m left`;
  if (hrs < 48) return `${hrs}h ${mins}m left`;
  return `${Math.round(hrs / 24)}d left`;
}

const DEMO_TODAY_FOCUS: GameEvent = {
  id: "today-max-battle",
  date: "Today · 2, 5pm",
  start: "2026-04-25T14:00",
  end: "2026-04-25T17:00",
  title: "Max Battle Day",
  category: "max-battle",
  blurb: "Eternatus T6 raids, you're missing this one for your Main Dex.",
  featured: true,
};

const UPCOMING_IDS = ["steeled", "tapulele", "lechonk-cd", "may16-cd", "taken-over"];

interface LiveEventApi {
  id: string;
  name: string;
  heading?: string;
  category: string;
  rawType: string;
  start: string;
  end: string;
  link?: string;
  image?: string;
  // The feed serialises BoostedSpecies, which carries the resolved speciesId +
  // shinyBoost, not just a display name (see server/events.ts and
  // cloudflare/src/lib/events.ts). The blurb only needs the names; the per-event
  // "still missing from your dex" strip needs the ids, so the whole spawn is
  // threaded onto GameEvent by liveToGameEvent below.
  spawns?: EventSpawn[];
  // Bonus lines the feed states ("2× Catch Candy") — see LiveEvent.bonuses in
  // cloudflare/src/lib/events.ts. Threaded onto GameEvent for the badge row.
  bonuses?: string[];
  /** One-place event (worker lib/eventGeo, 2026-10-23); see GameEvent.geoLocked. */
  geoLocked?: boolean;
  place?: string;
}
interface LiveEventsResponse {
  active: LiveEventApi[];
  upcoming: LiveEventApi[];
  /**
   * Every one-place event that is on or coming, attended or not, so the "In
   * person" section can offer the switch. The attended ones ALSO appear in
   * active / upcoming (the server keys that on the `attending=` ids the
   * device sends, see eventsLiveKey). Absent from older workers.
   */
  inPerson?: Array<LiveEventApi & { attending?: boolean }>;
  attribution: { source: string; sourceUrl: string; feed: string; feedUrl: string };
  meta: { fetchedAt: string | null; cachedEventCount: number };
}

// Map a LeekDuck event to the GameEvent shape used by the Calendar UI.
//
// EVERY PIECE OF FEED TEXT IS DECODED HERE TOO, not only in the worker that
// serves it. The feed is scraped HTML, so titles arrive carrying entities
// ("PokémonXP &amp; 2026 Worlds"); the worker now decodes on ingest, but its
// responses are cached and a deploy does not reach every device at once, so the
// client refuses to render what it is handed without checking. Decoding is
// idempotent, which is what makes belt-and-braces safe here. See
// @shared/eventText.
function liveToGameEvent(ev: LiveEventApi): GameEvent {
  const start = eventCountdownDate(ev.start);
  const dateLabel = Number.isNaN(start.getTime())
    ? ev.start
    : start.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const cat = (ev.category as GameEvent["category"]) ?? "event";
  const name = decodeEventTextOptional(ev.name);
  const heading = decodeEventTextOptional(ev.heading);
  const spawns = (ev.spawns ?? []).map((s) =>
    s && typeof s.name === "string" ? { ...s, name: decodeEventText(s.name) } : s,
  );
  const spawnNames = spawns
    .map((s) => s?.name)
    .filter((n): n is string => typeof n === "string" && n.length > 0)
    .slice(0, 3);
  const blurb = spawnNames.length > 0
    ? `Featured spawns, ${spawnNames.join(", ")}.`
    : (heading ?? name ?? "");
  return {
    id: ev.id,
    date: dateLabel,
    start: ev.start,
    end: ev.end,
    title: name ?? ev.rawType ?? "Event",
    category: cat,
    blurb,
    image: ev.image,
    // PR-E10 fix 1: thread the LeekDuck deep-link through so the
    // "View on LeekDuck" footer links to the event page directly, not the
    // events index.
    link: ev.link,
    // Full spawn list (with speciesId / shinyBoost / admin variant overrides)
    // so each card can cross-reference the event against the trainer's own dex.
    spawns,
    // Same belt-and-braces decode as the title: bonus strings are scraped text.
    bonuses: ev.bonuses?.map((b) => decodeEventText(b)),
    ...(ev.geoLocked ? { geoLocked: true } : {}),
    ...(ev.place ? { place: decodeEventText(ev.place) } : {}),
  };
}

// PR-E10 fix 1: every LeekDuck deep-link falls back to the events index when
// a feed entry is missing `link`. Centralised here so all event cards agree.
const LEEKDUCK_FALLBACK = "https://leekduck.com/events/";
function leekduckHref(ev: GameEvent): string {
  return ev.link && ev.link.length > 0 ? ev.link : LEEKDUCK_FALLBACK;
}

// Capacitor Share throws when the user dismisses the share sheet. We treat
// that as a no-op so the destructive toast only surfaces for genuine errors.
function isShareCancellation(err: unknown): boolean {
  if (!err) return false;
  const msg = err instanceof Error ? err.message : String(err);
  const lower = msg.toLowerCase();
  return (
    lower.includes("canceled") ||
    lower.includes("cancelled") ||
    lower.includes("share canceled") ||
    lower.includes("share cancelled") ||
    lower.includes("user dismissed")
  );
}

export default function CalendarPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  // Pro: per-event "what am I still missing from this event?" strips, with one
  // switch at the very top of the page that shows/hides every one of them. The
  // choice persists (eventDexGapsPref) so it survives a relaunch. Free trainers
  // see the locked upsell in place of the switch and get no strips.
  //
  // Gating history, because this has moved twice: it shipped Pro, went BASIC on
  // 2026-08-07 (the event × your-own-dex comparison being the download hook),
  // and is Pro again from 2026-08-07 on the owner's direction. The FRIENDS half
  // (Profile's "Trades to chase") has been Pro throughout.
  const { tier } = useSession();
  const showDexGaps = useEventDexGapsPref();
  const isPro = hasProAccess(tier);
  // Free trainers still get a row per event — the TEASER, which counts their real
  // gaps without naming them. So the strip mounts for every tier and `locked`
  // decides which form it takes. The pref only governs Pro (a free trainer has no
  // switch to flip, and the default is on).
  const canSeeDexGaps = isPro ? showDexGaps : true;
  // b15b U6: persistent plan is the single source of truth for "planned"
  // membership now (replacing the in-memory session Set), so the plan survives
  // reloads and is recallable from any Calendar sub-screen.
  const todos = useCalendarTodos();
  const plannedIds = useMemo(() => new Set(todos.map((t) => t.eventId)), [todos]);
  const planEvent = (ev: GameEvent) =>
    toggleCalendarTodo({
      eventId: ev.id,
      date: ev.date,
      title: ev.title,
      start: ev.start,
      end: ev.end,
    });
  const [bannerVisible, setBannerVisible] = useState(false);
  const bannerSeen = useRef(false);
  const upcomingScroll = useRef<HTMLDivElement>(null);
  // b18f item 2: LeekDuck-style category filter. "all" plus one pill per
  // category actually present in today's focus + upcoming list. Filters BOTH
  // the Today focus card and the Upcoming list; active pill carries the
  // category's own `--event-color-*` token so it stays theme-aware.
  const [eventFilter, setEventFilter] = useState<GameEvent["category"] | "all">("all");
  // E47: the id of the event a tap is waiting to scroll to. Forcing the filter
  // to "all" re-renders and mounts the target row, so the scroll cannot run in
  // the same tick as setEventFilter; we stash the event id here and a useEffect
  // performs the scroll once the row exists (deterministic, no rAF guesswork).
  const pendingScrollEventId = useRef<string | null>(null);
  // The event a hand-off from another page asked for (2026-09-07): a "Now"
  // card can name an ACTIVE event that is not today's focus, and the list
  // below renders only the focus plus upcoming, so that row never existed
  // and the hand-off could not land. Held in state so the list can add it.
  const [handOffEventId, setHandOffEventId] = useState<string | null>(null);

  // The one-place events this device is attending (lib/eventAttending). Read
  // live so a flip of the switch re-renders with a new feed key (the ids ride
  // on the request) and the event joins or leaves Now / Upcoming.
  const attendingIds = useAttendingIds();
  const attendingSet = useMemo(() => new Set(attendingIds), [attendingIds]);
  // The account's list joins this device's on first open (a fresh install,
  // or a switch flipped on another phone); never removes a local id.
  useEffect(() => {
    void hydrateAttendingFromAccount();
  }, []);

  // Live LeekDuck feed (via ScrapedDuck), attribution required.
  const liveQ = useQuery<LiveEventsResponse>({
    queryKey: [eventsLiveKey()],
  });

  const liveActive = useMemo(
    () => (liveQ.data?.active ?? []).map(liveToGameEvent),
    [liveQ.data?.active],
  );
  const liveUpcoming = useMemo(
    () => (liveQ.data?.upcoming ?? []).map(liveToGameEvent),
    [liveQ.data?.upcoming],
  );

  // The automatic countdowns (lib/liveEventPrefs, 2026-10-30): once the feed
  // is here, arm this phone for every upcoming event the standing choice
  // covers. A no-op on an iPhone the Worker's push looks after, and when
  // nothing new needs arming.
  useEffect(() => {
    if (!liveQ.data) return;
    void armSubscribedEvents([...liveActive, ...liveUpcoming]);
  }, [liveQ.data, liveActive, liveUpcoming]);
  const inPerson = useMemo(
    () => (liveQ.data?.inPerson ?? []).map(liveToGameEvent),
    [liveQ.data?.inPerson],
  );

  // In prod we surface the live feed. Outside prod (design QA / demo mode)
  // we keep the curated demo timeline so the screenshots still look right.
  // Today's event is picked by the DEVICE clock across both buckets (see
  // pickTodayFocus): the server's bucket boundary lags a fest that opened at
  // 10 a.m. local, and a month-long research is not today's event.
  const todayFocus: GameEvent | null = import.meta.env.PROD
    ? pickTodayFocus(liveActive, liveUpcoming)
    : DEMO_TODAY_FOCUS;
  // An active event a hand-off targets leads the list (it is on now), unless
  // it is already the focus or in the upcoming bucket.
  const handOffActive: GameEvent | null =
    handOffEventId && handOffEventId !== todayFocus?.id && !liveUpcoming.some((e) => e.id === handOffEventId)
      ? liveActive.find((e) => e.id === handOffEventId) ?? null
      : null;
  const upcoming: GameEvent[] = import.meta.env.PROD
    ? [...(handOffActive ? [handOffActive] : []), ...liveUpcoming.filter((e) => e.id !== todayFocus?.id)]
    : EVENTS.filter((e) => UPCOMING_IDS.includes(e.id));
  const attribution = liveQ.data?.attribution;

  // b18f item 2: categories present across the focus + upcoming events, in a
  // stable order, so we only render a pill for a category that has events.
  const filterCategories = useMemo(() => {
    const seen: GameEvent["category"][] = [];
    for (const ev of [todayFocus, ...upcoming]) {
      if (ev && !seen.includes(ev.category)) seen.push(ev.category);
    }
    return seen;
  }, [todayFocus, upcoming]);

  // Reset to "all" if the selected category drops out of the feed (e.g. an
  // event wraps up), so the user is never stuck on an empty filter.
  useEffect(() => {
    if (eventFilter !== "all" && !filterCategories.includes(eventFilter)) {
      setEventFilter("all");
    }
  }, [eventFilter, filterCategories]);

  const matchesFilter = (ev: GameEvent) =>
    eventFilter === "all" || ev.category === eventFilter;
  const visibleUpcoming = upcoming.filter(matchesFilter);
  const todayMatchesFilter = !todayFocus || matchesFilter(todayFocus);

  // Fire the demo notification banner once on first visit (non-prod only).
  useEffect(() => {
    if (import.meta.env.PROD) return;
    if (bannerSeen.current) return;
    bannerSeen.current = true;
    const showT = setTimeout(() => setBannerVisible(true), 600);
    const hideT = setTimeout(() => setBannerVisible(false), 5500);
    return () => {
      clearTimeout(showT);
      clearTimeout(hideT);
    };
  }, []);

  async function downloadFocusIcs() {
    if (!todayFocus) return;
    try {
      await shareCalendarInvite({
        uid: todayFocus.id,
        title: `Pokémon GO · ${todayFocus.title}`,
        description: todayFocus.blurb,
        location: "Pokémon GO",
        start: todayFocus.start,
        end: todayFocus.end,
      });
    } catch (err) {
      if (isShareCancellation(err)) return;
      console.error("[calendar-invite] share failed", err);
      toast({ title: "Could not open calendar", variant: "destructive" });
    }
  }

  function scrollToEvent(id: string) {
    const el = document.querySelector(`[data-testid="event-card-${id}"]`) as HTMLElement | null;
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // Events the strip + planned list draw from: today's focus plus upcoming.
  const stripEvents = useMemo(
    () => [todayFocus, ...upcoming].filter((e): e is GameEvent => !!e),
    [todayFocus, upcoming],
  );

  // E34/E47: tapping a day cell lands the event list on that day's resolved
  // event at the top of the screen. Rows carry a stable id={eventDomId(ev.id)}
  // so the tile lands on that EXACT event. We nudge up by the sticky offset
  // (safe-area + 56px header + the ~48px pinned filter row) so the target sits
  // just below the pinned stack, not under it.
  // 164, not 112 (2026-08-21 device pass): the day-strip tap was landing the
  // card's image under the sticky header + filter-pill stack — one more
  // spacing step below the pills clears it.
  const STICKY_SCROLL_OFFSET_PX = 164;

  // E47: perform the actual scroll for a now-mounted event row. Returns false if
  // the row is not in the DOM yet (the effect retries after the next commit).
  function performScrollToEvent(eventId: string, behavior: ScrollBehavior = "smooth"): boolean {
    const el = document.getElementById(eventDomId(eventId));
    if (!el) return false;
    // scrollIntoView, NOT window.scrollTo (2026-08-21 device report: tapping a
    // day-strip cell did nothing). The page scrolls inside an inner container,
    // so window.scrollTo was a silent no-op — scrollIntoView asks whichever
    // ancestor actually scrolls. scroll-margin-top keeps the card clear of the
    // sticky header, which is what the manual offset arithmetic was for.
    el.style.scrollMarginTop = `${STICKY_SCROLL_OFFSET_PX}px`;
    el.scrollIntoView?.({ behavior, block: "start" });
    return true;
  }

  // E47/E49/E51: day-cell TAP path. Resolves the day to an event that either
  // STARTS on it or is ONGOING through it (a multi-day event), then lands the
  // list on THAT EXACT event's row (by event id, not by day — a day-keyed
  // lookup collided when two events shared a start day and sent the tap to the
  // wrong card). A day with no starting or ongoing event simply does nothing —
  // we no longer surface a "no events on <day>" cue, which was misleading
  // because an event can be running on a day it does not start. Forcing the
  // filter to "all" guarantees the target row is rendered even when an
  // off-category filter is active, then the pending-scroll effect lands on it
  // post-commit.
  function scrollToDay(isoDay: string) {
    const target = findActiveDayEvent(stripEvents, isoDay);
    if (!target) return;
    if (eventFilter !== "all") {
      // Defer: setEventFilter re-renders and mounts off-category rows; the
      // effect scrolls once the row exists.
      pendingScrollEventId.current = target.id;
      setEventFilter("all");
      return;
    }
    // Filter is already "all": the row is mounted, scroll now (fall back to the
    // pending path if a synchronous lookup misses for any reason).
    if (!performScrollToEvent(target.id)) {
      pendingScrollEventId.current = target.id;
    }
  }

  // A hand-off from another page (a Profile event card, the Shiny Hunt, the
  // Up next row): land on that event's row the same way a day tap does. The
  // pending effect below retries after every commit, so the row can arrive
  // with the feed a moment after mount. Forcing the filter to "all" keeps an
  // off-category row from being filtered out of existence.
  const [, bumpForHandOff] = useState(0);
  useEffect(() => {
    const id = consumeCalendarEvent();
    if (!id) return;
    pendingScrollEventId.current = id;
    setHandOffEventId(id);
    if (eventFilter !== "all") setEventFilter("all");
    else bumpForHandOff((n) => n + 1);
    // Re-assert for a moment (2026-09-04 device report: the tap did not
    // land). The row scrolled into view once, then images above it loaded
    // and the shell's route nudge ran, and the page was elsewhere again.
    // A few later asserts keep the row in place until the layout settles.
    // The landing is INSTANT, not smooth (2026-09-07): a smooth glide was
    // aborted by the shell's 50 ms route nudge and fought frame by frame by
    // the route reset's scroll guard (now held off by armCalendarEvent), and
    // an arrival from another page has no motion to sell anyway. After the
    // last re-assert the hand-off gives up: an event the Calendar never
    // renders (an active event that is not today's focus) must not leave
    // the every-commit retry querying the DOM for the life of the page.
    const HANDOFF_ASSERT_MS = [250, 600, 1100, 1800];
    const timers = HANDOFF_ASSERT_MS.map((ms, i) =>
      window.setTimeout(() => {
        if (pendingScrollEventId.current === id || document.getElementById(eventDomId(id))) {
          if (performScrollToEvent(id, "auto")) pendingScrollEventId.current = null;
        }
        if (i === HANDOFF_ASSERT_MS.length - 1 && pendingScrollEventId.current === id) {
          pendingScrollEventId.current = null;
        }
      }, ms),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // E47: run the deferred scroll after the filter-forced re-render commits.
  useEffect(() => {
    const id = pendingScrollEventId.current;
    if (id === null) return;
    if (performScrollToEvent(id, "auto")) pendingScrollEventId.current = null;
  });

  async function downloadEventIcs(ev: GameEvent) {
    try {
      await shareCalendarInvite({
        uid: ev.id,
        title: `Pokémon GO · ${ev.title}`,
        description: ev.blurb,
        location: "Pokémon GO",
        start: ev.start,
        end: ev.end,
      });
    } catch (err) {
      if (isShareCancellation(err)) return;
      console.error("[calendar-invite] share failed", err);
      toast({ title: "Could not open calendar", variant: "destructive" });
    }
  }

  return (
    <AppShell pageSpacing>
      {/* Notification banner, slides down from the top */}
      <NotifBanner
        visible={bannerVisible}
        onDismiss={() => setBannerVisible(false)}
        onTap={() => {
          setBannerVisible(false);
          // Scroll to upcoming CD card
          setTimeout(() => {
            const el = document.querySelector('[data-testid="event-card-lechonk-cd"]') as HTMLElement | null;
            el?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100);
        }}
      />

      {/* b13i I1: decorative inverse-theme dot-rail down the page gutter. */}
      <DotRail>
      {/* b17m D: pt-12 top spacing so the Calendar eyebrow pill sits fully
          visible below the safe-area + header, matching Profile (pt-12). */}
      <div className="pt-12 page-hero-top">
      <MenuHeading
        eyebrow="Calendar"
        title="Key Events."
        greenSuffix="Prioritised."
        data-dotrail-skip
      />
      </div>

      {/* "What I'm missing" master switch. Sits at the very top of the page, above
          every event surface, because it governs all of them at once. */}
      <div data-dotrail-dot>
        <DexGapsToggle />
      </div>

      {/* b16 item 6: the full-month grid (b15b U7) was reverted; the seven-day
          strip is once again the primary at-a-glance calendar surface. */}
      <div data-dotrail-dot>
        <DayStrip events={stripEvents} onPickDay={scrollToDay} />
      </div>

      {/* b13d S5 / b15b U6: recallable plan, persisted in localStorage and keyed
          by event + date. Reads straight from the store so it survives reloads
          and is recallable from any Calendar sub-screen. */}
      {/* Wrapped for the rail dot: PlannedList renders its own <section>, and
          DotRail reads JSX props, so a marker inside it would be invisible. */}
      <div data-dotrail-dot>
        <PlannedList
          todos={todos}
          onRemove={(id) => removeCalendarTodo(id)}
          onPick={scrollToEvent}
        />
      </div>

      {/* b18f item 2: sticky LeekDuck-style category filter. Pins just below the
          AppShell header (56px) as the events list scrolls, mirroring the b18d
          Dex slim bar. Selecting a pill filters both the Today focus and the
          Upcoming list. */}
      {filterCategories.length > 1 && (
        <EventFilterRow
          categories={filterCategories}
          active={eventFilter}
          onSelect={setEventFilter}
        />
      )}

      {/* 1, Today */}
      {todayMatchesFilter && (
      <section className="mt-6" data-dotrail-dot>
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2">
          Today · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
        </p>
        <TodayDailyBonuses />
        {todayFocus ? (
          <FocusCard
            ev={todayFocus}
            isPlanned={plannedIds.has(todayFocus.id)}
            onTogglePlanned={() => planEvent(todayFocus)}
            onAddToCal={downloadFocusIcs}
            showDexGaps={canSeeDexGaps}
            gapsLocked={!isPro}
          />
        ) : (
          <div className="rounded-2xl p-5 surface-elev text-center" data-testid="calendar-empty-today">
            <p className="text-sm font-semibold">No live event right now</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {liveQ.isLoading ? "Loading the live feed..." : "Check back soon for the next live event."}
            </p>
          </div>
        )}
      </section>
      )}

      {/* 2, Upcoming Events */}
      <section className="mt-7" data-dotrail-dot>
        <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-3 inline-flex items-center gap-1.5">
          <CalendarIcon className="w-3.5 h-3.5" /> Upcoming events
        </h3>
        <div ref={upcomingScroll} className="space-y-3">
          {visibleUpcoming.length === 0 ? (
            <div className="rounded-2xl p-4 surface-elev text-center" data-testid="calendar-empty-upcoming">
              <p className="text-[11px] text-muted-foreground">
                {liveQ.isLoading
                  ? "Loading upcoming events..."
                  : eventFilter !== "all"
                    ? "No upcoming events in this category."
                    : "No upcoming events in the next 7 days."}
              </p>
            </div>
          ) : (
            visibleUpcoming.map((ev) => (
              <EventCard
                key={ev.id}
                ev={ev}
                isPlanned={plannedIds.has(ev.id)}
                onTogglePlanned={() => planEvent(ev)}
                onAddToCal={() => downloadEventIcs(ev)}
                showDexGaps={canSeeDexGaps}
                gapsLocked={!isPro}
              />
            ))
          )}
        </div>
        {attribution && (
          <p className="mt-3 text-[10px] text-muted-foreground/80" data-testid="events-attribution">
            Event data from{" "}
            <a href={attribution.sourceUrl} target="_blank" rel="noreferrer" className="underline">
              {attribution.source}
            </a>{" "}
            via{" "}
            <a href={attribution.feedUrl} target="_blank" rel="noreferrer" className="underline">
              {attribution.feed}
            </a>.
          </p>
        )}
      </section>

      {/* 3, In person: the one-place events (City Safari, a Wild Area city, a
          Night Out). Hidden from Now / Upcoming for everyone who is not there;
          the switch here is what puts one in (2026-10-23 owner ask). */}
      {inPerson.length > 0 && (
        <div data-dotrail-dot>
          <InPersonSection events={inPerson} attending={attendingSet} onToggle={(ev) => toggleAttending(ev.id)} />
        </div>
      )}

      {/* The Quick Update catch-up card was REMOVED from this page
          (2026-08-21 ask) — it surfaces on the Profile page only. See
          EventCatchUp. */}

      <div className="mt-10" data-dotrail-skip />
      </DotRail>
    </AppShell>
  );
}

// Today's Daily Discovery (Friendship Friday, Max Monday, ...): the recurring
// day-of-week bonuses live from local midnight to local midnight, so the
// Calendar's Today section names the day's theme and its bonus pills above the
// focus card. Derived from the DEVICE clock (activeDailyBonusEvents), so the
// card flips with the trainer's own day of week; renders nothing on days with
// no Daily Discovery (Saturday). Same registry that feeds the Bonus bubble and
// the Bonuses page, so the surfaces can never disagree.
function TodayDailyBonuses() {
  const daily = activeDailyBonusEvents();
  if (daily.length === 0) return null;
  return (
    <div className="mb-3 space-y-2" data-testid="calendar-daily-bonuses">
      {daily.map((ev) => (
        <div
          key={ev.id}
          className="rounded-2xl p-3 surface-elev"
          data-testid={`calendar-daily-bonus-${ev.id}`}
        >
          <p className="text-[11px] font-bold inline-flex items-center gap-1.5 text-[var(--text-primary)]">
            <Gift className="w-3.5 h-3.5" style={{ color: "hsl(var(--bonus))" }} aria-hidden />
            {ev.name}
            <span className="font-normal text-[10px] text-muted-foreground">
              All day, local time
            </span>
          </p>
          <BonusBadges bonuses={mergedBonuses(ev)} max={4} testId={`calendar-daily-pills-${ev.id}`} />
        </div>
      ))}
    </div>
  );
}

// Subscribe to the persisted "show what I'm missing" pref. Re-reads on the
// EVENT_DEX_GAPS_EVENT broadcast so every Calendar surface flips together and
// the choice survives a reload.
function useEventDexGapsPref(): boolean {
  const [enabled, setEnabled] = useState(() => isEventDexGapsEnabled());
  useEffect(() => {
    if (typeof window === "undefined") return;
    const refresh = () => setEnabled(isEventDexGapsEnabled());
    refresh();
    window.addEventListener(EVENT_DEX_GAPS_EVENT, refresh);
    return () => window.removeEventListener(EVENT_DEX_GAPS_EVENT, refresh);
  }, []);
  return enabled;
}

// The page-level master switch for the per-event "still missing from your dex"
// strips. Pro-gated: a free trainer sees the ProLock upsell card in place of the
// switch, and no strips render anywhere on the page.
// Exported for the Pro-gating test (mirrors EventFilterRow above).
export function DexGapsToggle() {
  const { tier } = useSession();
  const enabled = useEventDexGapsPref();
  const isPro = hasProAccess(tier);

  // mt-1 on both branches: this is the first block under the page heading,
  // and it should hug it like later blocks hug each other (2026-08-21 ask).
  if (!isPro) {
    return (
      <section className="mt-1" data-testid="calendar-dex-gaps-locked">
        <ProLock
          feature="event-dex-gaps"
          source="event-dex-gaps"
          blurb="See exactly which Pokémon from each event you're still missing, right on the event card, so you know which ones are worth showing up for. Included with Pro."
        />
      </section>
    );
  }

  return (
    <section className="mt-1" data-testid="calendar-dex-gaps-toggle">
      <div
        className="rounded-xl px-3 py-2.5 flex items-center gap-3"
        style={{
          background: "var(--surface-raised)",
          border: "1px solid var(--surface-chip-border)",
        }}
      >
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-semibold text-[var(--text-primary)]">
            Show what I'm missing
          </p>
          <p className="text-[10.5px] text-muted-foreground leading-snug">
            Lists the Pokémon you still need from each event below.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(v) => setEventDexGapsEnabled(v)}
          aria-label="Show the Pokémon you are still missing under each event"
          data-testid="toggle-event-dex-gaps"
        />
      </div>
    </section>
  );
}

// b13d S5: seven-day strip (pogocalendar-style). Maps the next 7 calendar
// days onto cells; a day that hosts an event shows its artwork (live feed)
// or a category-tinted dot, and tapping scrolls to that event card below.
const STRIP_DAYS = 7;

function startOfDay(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function localIsoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function DayStrip({
  events,
  onPickDay,
}: {
  events: GameEvent[];
  // E34/E47: receives the cell's local wall-clock day (YYYY-MM-DD). The parent
  // forces the filter to "all", then scrolls to that exact day's section; a day
  // with no events shows a subtle cue instead of scrolling.
  onPickDay: (isoDay: string) => void;
}) {
  const today = startOfDay(new Date());
  const days = useMemo(() => {
    return Array.from({ length: STRIP_DAYS }, (_, i) => {
      const date = new Date(today + i * 86_400_000);
      const iso = localIsoDay(date);
      // Match the cell to an event by local wall-clock day (E32-consistent),
      // not by re-parsing the instant, so the artwork/dot on a cell agrees with
      // the row the tap scrolls to. E50: the visible marker is START-DAY ONLY —
      // a cell shows artwork/a dot only when an event STARTS on that day. Days
      // that merely sit inside an ongoing multi-day event's run stay visually
      // blank, since marking them implied an event began there. The tap path
      // (scrollToDay) still uses findActiveDayEvent, so tapping an interior day
      // of a run still navigates to that event.
      const hit = findExactDayEvent(events, iso) ?? undefined;
      return { date, iso, event: hit };
    });
  }, [events, today]);

  return (
    <section className="mt-5" data-testid="calendar-day-strip">
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2">
        Next 7 days
      </p>
      <div className="grid grid-cols-7 gap-1.5">
        {days.map(({ date, iso, event }, i) => {
          const isToday = i === 0;
          return (
            <button
              key={date.toISOString()}
              type="button"
              // E34/E47: every cell is tappable. A day with no event of its
              // own shows a subtle cue (it does not jump to another day).
              onClick={() => onPickDay(iso)}
              data-testid={`day-strip-cell-${i}`}
              data-event-date={iso}
              // E48: the whole cell is the tap target (not just the thumbnail),
              // and the accessible label names the event it links to so the cell
              // does not read as a bare "S 21" to assistive tech.
              aria-label={dayCellAccessibleLabel(date, event?.title)}
              className="rounded-xl p-1.5 flex flex-col items-center gap-1 transition disabled:opacity-100 cursor-pointer"
              style={{
                background: isToday ? "var(--accent-subtle)" : "var(--surface-raised)",
                border: isToday
                  ? "1px solid var(--surface-chip-border)"
                  : "1px solid var(--border-subtle)",
              }}
            >
              <span className="text-[9px] font-semibold uppercase text-muted-foreground">
                {date.toLocaleDateString("en-US", { weekday: "narrow" })}
              </span>
              <span className="text-[13px] font-bold tabular-nums text-[var(--text-primary)]">
                {date.getDate()}
              </span>
              <span className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
                {event?.image ? (
                  <img
                    src={event.image}
                    alt=""
                    className="w-full h-full object-cover rounded-lg"
                    draggable={false}
                  />
                ) : event ? (
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: "var(--accent-fill)" }}
                    aria-hidden
                  />
                ) : (
                  <span className="w-2 h-2 rounded-full bg-transparent" aria-hidden />
                )}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// b13d S5 / b15b U6: recallable to-do list of events the trainer has planned.
// Reads from the persistent calendarTodoStore (localStorage), so re-opening the
// tab — or relaunching the app — still shows what they committed to. Ticking
// the checkbox removes the item from the plan entirely (done = gone).
function PlannedList({
  todos,
  onRemove,
  onPick,
}: {
  todos: CalendarTodoItem[];
  onRemove: (id: string) => void;
  onPick: (eventId: string) => void;
}) {
  if (todos.length === 0) return null;
  return (
    <section className="mt-7" data-testid="calendar-planned-list">
      <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-3 inline-flex items-center gap-1.5">
        <Check className="w-3.5 h-3.5" /> Your plan
      </h3>
      <div className="space-y-2">
        {todos.map((t) => (
          <div
            key={t.id}
            data-testid={`planned-item-${t.eventId}`}
            className="rounded-xl p-3 flex items-center gap-3"
            style={{
              background: "var(--surface-raised)",
              border: "1px solid var(--surface-chip-border)",
            }}
          >
            <button
              type="button"
              onClick={() => onRemove(t.id)}
              data-testid={`planned-toggle-${t.eventId}`}
              aria-label="Tick off and remove from plan"
              className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
              style={{ background: "var(--accent-fill)", color: "var(--text-on-accent)" }}
            >
              <Check className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onPick(t.eventId)}
              className="min-w-0 flex-1 text-left"
            >
              <p className="text-[13px] font-semibold leading-tight truncate text-[var(--text-primary)]">
                {t.title}
              </p>
              <p className="text-[10.5px] text-muted-foreground mt-0.5">{t.date}</p>
            </button>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          </div>
        ))}
      </div>
    </section>
  );
}

// b13g G1: Today's Focus is the flagship HeroCard — full-bleed artwork, a
// "RIGHT NOW" / "TODAY" status badge beside the type pill, large title, and a
// countdown footer. Same shared component as the Upcoming rows, bolder glow.
function FocusCard({
  ev, isPlanned, onTogglePlanned, onAddToCal, showDexGaps, gapsLocked,
}: {
  ev: GameEvent;
  isPlanned: boolean;
  onTogglePlanned: () => void;
  onAddToCal: () => void;
  /** Pro + the page toggle is on: render the "still missing" strip. */
  showDexGaps?: boolean;
  /** Free tier: render the counting teaser instead of the named gaps. */
  gapsLocked?: boolean;
}) {
  const glow = isPlanned ? "var(--accent-fill)" : eventGlowColor(ev.category);
  const countdown = eventCountdown(ev);
  const isActive = (() => {
    const s = eventCountdownMs(ev.start);
    const e = ev.end ? eventCountdownMs(ev.end) : NaN;
    const t = Date.now();
    return !Number.isNaN(s) && s <= t && (Number.isNaN(e) || e >= t);
  })();
  return (
    <HeroCard
      data-testid="card-today-focus"
      id={eventDomId(ev.id)}
      data-event-date={eventLocalDate(ev)}
      glow="bold"
      glowColor={glow}
      media={
        <HeroCardMedia
          src={ev.image}
          alt={ev.title}
          testId={`event-image-${ev.id}`}
          className="h-44"
          fallback={<Sparkles className="w-10 h-10 text-[var(--accent-fill)]" />}
        />
      }
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-[11px] text-[var(--text-secondary)]" data-testid="event-countdown-today">
            {countdown ?? ev.date}
          </span>
          <a
            href={leekduckHref(ev)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] no-underline"
            data-testid="event-leekduck-today"
          >
            <ExternalLink className="w-3 h-3" /> View on LeekDuck
          </a>
        </div>
      }
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
          style={{ color: glow, background: `color-mix(in srgb, ${glow} 16%, transparent)`, border: `1px solid color-mix(in srgb, ${glow} 42%, transparent)` }}
        >
          {isPlanned ? <><Check className="w-3 h-3" /> Planned</> : isActive ? "Right now" : "Today"}
        </span>
        <EventTypePill category={ev.category} />
      </div>

      <h3 className="mt-2 text-xl font-extrabold leading-tight">{ev.title}</h3>
      <p className="mt-1.5 text-[12px] text-[var(--text-secondary)] leading-snug">{ev.blurb}</p>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={onTogglePlanned}
          data-testid="button-mark-planned-today"
          className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold hover-elevate"
          style={isPlanned
            ? { background: "hsl(140 70% 50% / 0.18)", border: "1px solid hsl(140 70% 50% / 0.5)", color: "hsl(140 70% 60%)" }
            : { background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}
        >
          {isPlanned ? <><Check className="w-3.5 h-3.5" /> Planned</> : "✓ Mark as planned"}
        </button>
        <AddToCalendarMenu
          event={{
            title: `Pokémon GO · ${ev.title}`,
            description: ev.blurb,
            location: "Pokémon GO",
            start: ev.start,
            end: ev.end,
          }}
          onApple={onAddToCal}
          testId="button-addtocal-today"
          className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold hover-elevate"
          style={{ background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}
        />
        <EventRemindButton
          event={{ id: ev.id, title: ev.title, start: ev.start, end: ev.end, date: ev.date, category: ev.category, spawns: ev.spawns, bonuses: ev.bonuses, geoLocked: ev.geoLocked }}
          testId="button-remind-today"
        />
      </div>

      {/* What this event is worth playing for: feed + curated bonus pills.
          Renders nothing when the event states no bonuses. */}
      <BonusBadges bonuses={mergedBonuses(ev)} testId={`event-bonuses-${ev.id}`} />

      {showDexGaps && (
        <EventDexGaps
          eventId={ev.id}
          spawns={ev.spawns ?? []}
          locked={gapsLocked}
          impliedDynamax={isMaxBattleEventCategory(ev.category)}
        />
      )}
    </HeroCard>
  );
}

// b13e B1: restore the per-event image on each event card. Binds to the
// GameEvent.image field (populated from the live LeekDuck feed via
// liveToGameEvent). Falls back to a token-driven neon silhouette so every
// row shows a recognisable "hero mon" shape when the asset is missing.
export function EventThumb({ ev, size = "md" }: { ev: GameEvent; size?: "sm" | "md" }) {
  const [failed, setFailed] = useState(false);
  const dim = size === "md" ? "w-14 h-14" : "w-11 h-11";
  if (ev.image && !failed) {
    return (
      <img
        src={ev.image}
        alt={ev.title}
        loading="lazy"
        draggable={false}
        onError={() => setFailed(true)}
        data-testid={`event-image-${ev.id}`}
        className={`${dim} rounded-xl object-cover shrink-0 border border-[var(--border-subtle)]`}
      />
    );
  }
  return (
    <span
      data-testid={`event-image-${ev.id}`}
      aria-hidden
      className={`${dim} rounded-xl shrink-0 flex items-center justify-center overflow-hidden border border-[var(--border-subtle)]`}
      style={{ background: "var(--accent-subtle)" }}
    >
      <Sparkles className="w-5 h-5 text-[var(--accent-fill)]" />
    </span>
  );
}

// b13g G1: the Upcoming-events row is now a full HeroCard matching the OLD
// winning layout (IMG_8805): full-bleed artwork band, status badge + type
// pill below the image, a large 2-line-capable title, a countdown + LeekDuck
// link footer, and a soft event-colour glow around the whole card.
function EventCard({
  ev, isPlanned, onTogglePlanned, onAddToCal, showDexGaps, gapsLocked,
}: {
  ev: GameEvent;
  isPlanned: boolean;
  onTogglePlanned: () => void;
  onAddToCal: () => void;
  /** Pro + the page toggle is on: render the "still missing" strip. */
  showDexGaps?: boolean;
  /** Free tier: render the counting teaser instead of the named gaps. */
  gapsLocked?: boolean;
}) {
  const glow = isPlanned ? "var(--accent-fill)" : eventGlowColor(ev.category);
  const countdown = eventCountdown(ev);
  return (
    <HeroCard
      data-testid={`event-card-${ev.id}`}
      id={eventDomId(ev.id)}
      data-event-date={eventLocalDate(ev)}
      glow={isPlanned ? "bold" : "soft"}
      glowColor={glow}
      media={
        <HeroCardMedia
          src={ev.image}
          alt={ev.title}
          testId={`event-image-${ev.id}`}
          className="h-36"
          fallback={<Sparkles className="w-8 h-8 text-[var(--accent-fill)]" />}
        />
      }
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-[11px] text-[var(--text-secondary)]" data-testid={`event-countdown-${ev.id}`}>
            {countdown ?? ev.date}
          </span>
          <a
            href={leekduckHref(ev)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] no-underline"
            data-testid={`event-leekduck-${ev.id}`}
          >
            <ExternalLink className="w-3 h-3" /> View on LeekDuck
          </a>
        </div>
      }
    >
      {/* Top-right corner, no row of its own (2026-09-03): the search icon
          opens the Nexus Dex with this event's Pokémon in the box. Featured
          steps left to make room when both are present. */}
      <EventSearchIcon
        eventId={ev.id}
        spawns={ev.spawns}
        className="absolute top-3 right-3 z-10"
      />
      {ev.featured && !isPlanned && (
        <span
          className="absolute top-3 z-10 px-1.5 py-px rounded text-[8px] font-bold tracking-wider uppercase text-[hsl(var(--shiny))] bg-[hsl(var(--shiny)/0.12)] border border-[hsl(var(--shiny)/0.35)]"
          style={{ right: eventDexSearchHref(ev.spawns) ? 44 : 12 }}
        >
          Featured
        </span>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {isPlanned ? (
          <span
            className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]"
            style={{ color: "var(--accent-fill)", background: "color-mix(in srgb, var(--accent-fill) 14%, transparent)", border: "1px solid color-mix(in srgb, var(--accent-fill) 38%, transparent)" }}
          >
            <Check className="w-3 h-3" /> Going
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]"
            style={{ color: glow, background: `color-mix(in srgb, ${glow} 14%, transparent)`, border: `1px solid color-mix(in srgb, ${glow} 38%, transparent)` }}
          >
            {ev.date}
          </span>
        )}
        <EventTypePill category={ev.category} />
        {ev.geoLocked && <PlacePill ev={ev} />}
      </div>

      <h4 className="mt-2 text-lg font-bold leading-tight">{ev.title}</h4>
      <p className="mt-1.5 text-[12px] text-[var(--text-secondary)] leading-snug">{ev.blurb}</p>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          onClick={onTogglePlanned}
          data-testid={`button-letsgo-${ev.id}`}
          className="rounded-full py-2 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition"
          style={isPlanned
            ? { background: "hsl(140 70% 50% / 0.18)", border: "1px solid hsl(140 70% 50% / 0.5)", color: "hsl(140 70% 60%)" }
            : { background: "var(--accent-fill)", color: "var(--text-on-accent)" }}
        >
          {isPlanned ? <><Check className="w-3.5 h-3.5" /> Going</> : <>Let's GO! <ChevronRight className="w-3 h-3" /></>}
        </button>
        <AddToCalendarMenu
          event={{
            title: `Pokémon GO · ${ev.title}`,
            description: ev.blurb,
            location: "Pokémon GO",
            start: ev.start,
            end: ev.end,
          }}
          onApple={onAddToCal}
          testId={`button-addtocal-${ev.id}`}
          className="w-full rounded-full py-2 text-xs font-semibold inline-flex items-center justify-center gap-1.5 hover-elevate"
          style={{ background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}
        />
              <EventRemindButton
          event={{ id: ev.id, title: ev.title, start: ev.start, end: ev.end, date: ev.date, category: ev.category, spawns: ev.spawns, bonuses: ev.bonuses, geoLocked: ev.geoLocked }}
          testId={`button-remind-${ev.id}`}
          className="col-span-2 rounded-full py-2 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition"
        />
</div>

      {/* What this event is worth playing for: feed + curated bonus pills.
          Renders nothing when the event states no bonuses. */}
      <BonusBadges bonuses={mergedBonuses(ev)} testId={`event-bonuses-${ev.id}`} />

      {showDexGaps && (
        <EventDexGaps
          eventId={ev.id}
          spawns={ev.spawns ?? []}
          locked={gapsLocked}
          impliedDynamax={isMaxBattleEventCategory(ev.category)}
        />
      )}
    </HeroCard>
  );
}

/** "Boston" / "In person", the one-place marker on an event card. */
function PlacePill({ ev }: { ev: GameEvent }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]"
      style={{ color: "var(--text-secondary)", background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}
      data-testid={`event-place-${ev.id}`}
    >
      <MapPin className="w-3 h-3" /> {ev.place ?? "In person"}
    </span>
  );
}

/**
 * The one-place events (2026-10-23 owner ask): a City Safari in Boston, a
 * Wild Area weekend in Sendai, a Night Out in London. The feed keeps them
 * out of Now / Upcoming for everyone who is not attending; this list is where
 * a trainer says they are, and the event then joins the lists above like any
 * other (with its gap strip, reminder and plan). Exported for its test.
 */
export function InPersonSection({
  events,
  attending,
  onToggle,
}: {
  events: GameEvent[];
  attending: ReadonlySet<string>;
  onToggle: (ev: GameEvent) => void;
}) {
  return (
    <section className="mt-7" data-testid="calendar-in-person">
      <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground mb-1 inline-flex items-center gap-1.5">
        <MapPin className="w-3.5 h-3.5" /> In person
      </h3>
      <p className="text-[11px] text-muted-foreground mb-3">
        Ticketed city events. Mark the ones you are going to and they join Now and Upcoming.
      </p>
      <div className="space-y-2">
        {events.map((ev) => {
          const on = attending.has(ev.id);
          return (
            <div
              key={ev.id}
              className="rounded-2xl p-3 surface-elev flex items-center gap-3"
              data-testid={`in-person-${ev.id}`}
              data-attending={on ? "true" : "false"}
            >
              <EventThumb ev={ev} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-tight truncate">{ev.title}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground inline-flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {ev.place ?? "In person"} · {ev.date}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onToggle(ev)}
                aria-pressed={on}
                data-testid={`button-attending-${ev.id}`}
                className="shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 transition"
                style={
                  on
                    ? { background: "var(--accent-fill)", color: "var(--text-on-accent)" }
                    : { background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)", color: "var(--text-secondary)" }
                }
              >
                {on ? <><Check className="w-3.5 h-3.5" /> Attending</> : "Attending?"}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// b18f item 2: sticky horizontal category filter for the Calendar events list.
// Reuses the shared EventTypePill taxonomy (eventTypeMeta) so each pill's colour
// is the category's own `--event-color-*` token, no new hard-coded colours. Pins
// below the 56px AppShell header so it persists while scrolling.
//
// b21: the picker-wheel physics + position-tracked selection ring were replaced
// by the shared <CenteredPillRow> (the same swap b20 made on the Dex variant
// tabs), which keeps the SELECTED pill locked in the centre using CSS
// scroll-snap plus a single scrollTo() on change. That removes the per-frame
// scroll math and ring-geometry measurement that made this row janky on device,
// along with the mid-scroll onSelect that re-rendered the whole events list on
// every pill crossing. Selection is now tap-only, so the filter can no longer
// change under a swipe.
export function EventFilterRow({
  categories,
  active,
  onSelect,
}: {
  categories: GameEvent["category"][];
  active: GameEvent["category"] | "all";
  onSelect: (key: GameEvent["category"] | "all") => void;
}) {
  // The "all" sentinel carries no category colour, so CenteredPillRow falls back
  // to the neutral --accent-fill for it.
  const items: CenteredPillItem[] = [
    { key: "all", label: "All" },
    ...categories.map((cat) => {
      const meta = eventTypeMeta(cat);
      return { key: cat, label: meta.label, color: `var(${meta.token})`, Icon: meta.icon };
    }),
  ];

  return (
    <CenteredPillRow
      items={items}
      activeKey={active}
      onSelect={(key) => onSelect(key as GameEvent["category"] | "all")}
      testId="calendar-event-filter"
      pillTestIdPrefix="calendar-filter"
      ariaLabel="Event type"
      size="sm"
      // Bleeds per side via `.sticky-band-bleed`, matching the Dex variant tabs
      // and the Dex ActionBar band: to the screen edge on the right, and only as
      // far as the DotRail gutter allows on the left, so the row STOPS beside
      // the spine rather than running across it. It used to bleed `-ml-8` (past
      // DotRail's `pl-5`) and hide the overlap under a 36px left fade, which put
      // the fade on top of the spine line + dots. Nothing overlaps the rail now,
      // so there is no mask.
      className="sticky sticky-band-bleed py-2 mt-4 items-center"
      style={{
        zIndex: STICKY_ROW_Z,
        // Flush against the header. This pinned at +56px until 2026-08-12,
        // which over-shot the 47px header and left a 9px sliver of page
        // scrolling through above the row. The Dex hit the identical bug and
        // was fixed on its own, so the correction never reached here — hence
        // the shared constant.
        top: STICKY_UNDER_HEADER,
        background: "var(--surface-chip-bg)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderBottom: "1px solid var(--border-subtle)",
      }}
    />
  );
}

function NotifBanner({
  visible, onDismiss, onTap, title,
}: { visible: boolean; onDismiss: () => void; onTap: () => void; title?: string }) {
  return (
    <div
      data-testid="notif-banner"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-[80] px-3 transition-all duration-500 pointer-events-none"
      style={{
        transform: visible ? "translateY(0)" : "translateY(-110%)",
        paddingTop: "calc(0.5rem + var(--nx-safe-top))",
      }}
    >
      <button
        onClick={onTap}
        className="mx-auto max-w-md w-full glass-strong rounded-2xl p-3 flex items-center gap-3 shadow-xl pointer-events-auto text-left"
        style={{ border: "1px solid hsl(var(--teal) / 0.4)" }}
      >
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "var(--accent-subtle)", border: "1px solid var(--border-subtle)" }}
        >
          <Bell className="w-4 h-4 text-[var(--accent-fill)]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">{title ?? (import.meta.env.PROD ? "Upcoming Community Day" : "Upcoming Community Day in 3 days")}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Tap to plan ahead.</p>
        </div>
        <span
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          className="w-7 h-7 rounded-full hover-elevate flex items-center justify-center shrink-0"
          aria-label="Dismiss"
          role="button"
        >
          <X className="w-3.5 h-3.5 text-muted-foreground" />
        </span>
      </button>
    </div>
  );
}
